import {ReactElement} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {CodeProbleme, LigneDePanier, LigneProblem} from 'api-types/ffconnect';
import {State} from '@synako/halitrek';
import {DisponibiliteArticleDesc} from 'api-types/ffconnect-hal';
import {dateAvecSlashes} from '../utils/date-tools';

export const usePanierAffichageBoutonsErreurs = ({
  ligneDePanier,
  probleme,
  dispo,
  tooltip,
  RemplacerErrorButton,
  DiffererErrorButton,
  SupprimerErrorButton,
  AjusterErrorButton,
  multipleCommandeClient = 1,
  isMarketplace = false,
  strong = <strong />,
  i = <i />,
}: {
  ligneDePanier: State<LigneDePanier>;
  probleme: LigneProblem | undefined;
  dispo: State<DisponibiliteArticleDesc> | undefined;
  tooltip: (label: string, message: string) => ReactElement;
  RemplacerErrorButton: ReactElement;
  DiffererErrorButton: ReactElement;
  SupprimerErrorButton: ReactElement;
  AjusterErrorButton: (quantiteMax: number | undefined) => ReactElement;
  multipleCommandeClient?: number;
  isMarketplace?: boolean;
  strong?: ReactElement;
  i?: ReactElement;
}) => {
  const {t} = useTranslation();
  const quantite = ligneDePanier.data.quantite;
  const uniteCommande = ligneDePanier.data.unite_de_commande_client;

  let errorCode: CodeProbleme = 'generique';
  let quantiteMax: number | undefined;

  if (probleme) {
    errorCode = probleme.problem.code;
    quantiteMax = probleme.problem.quantite_max;
  } else if (dispo) {
    if (dispo.data.tolede.disponibilite === 'rupture') {
      errorCode = 'rupture';
    } else if (dispo.data.tolede.disponibilite === 'delai_de_prepa_long') {
      // Prioritaire sur quantite_manquante : le cut-off n'est pas une rupture partielle
      errorCode = 'delai_de_prepa_long';
    } else if (dispo.data.quantite_manquante) {
      errorCode = 'quantite_trop_importante';
    }
    quantiteMax = dispo.data.quantite_livrable;
  }

  let message;
  let buttons;

  switch (errorCode) {
    case 'rupture':
      message = (
        <Trans
          t={t}
          components={{strong, i}}
        >
          {t('ligne_produit.indisponible.rupture', {
            quantite,
            unite: t(`unite.${uniteCommande}`, {count: quantite}),
          })}
        </Trans>
      );
      buttons = isMarketplace ? (
        <>{SupprimerErrorButton}</>
      ) : (
        <>
          {RemplacerErrorButton}
          {DiffererErrorButton}
          {SupprimerErrorButton}
        </>
      );
      break;
    case 'quantite_trop_importante': {
      // Arrondir à l'inférieur au multiple de commande valide
      const quantiteMaxAjustee =
        quantiteMax !== undefined
          ? quantiteMax - (quantiteMax % multipleCommandeClient)
          : 0;

      buttons = isMarketplace ? (
        <>
          {quantiteMaxAjustee > 0
            ? AjusterErrorButton(quantiteMaxAjustee)
            : null}
          {SupprimerErrorButton}
        </>
      ) : (
        <>
          {RemplacerErrorButton}
          {quantiteMaxAjustee > 0
            ? AjusterErrorButton(quantiteMaxAjustee)
            : SupprimerErrorButton}
        </>
      );
      message = (
        <Trans
          t={t}
          components={{strong, i}}
        >
          {t('ligne_produit.indisponible.rupture_partielle', {
            quantite_max: `${quantiteMax} ${t(`unite.${uniteCommande}`, {count: quantiteMax})}`,
            quantite_demande: quantite,
            count: quantiteMax,
          })}
        </Trans>
      );
      break;
    }
    case 'delai_de_prepa_long': {
      const prochaineDate =
        dispo?.data.tolede.prochaine_date_livraison_au_plus_tot;
      message = (
        <Trans
          t={t}
          components={{strong, i}}
        >
          {prochaineDate
            ? t('ligne_produit.indisponible.delai_de_prepa_long_avec_date', {
                date: dateAvecSlashes(prochaineDate),
              })
            : t('ligne_produit.indisponible.delai_de_prepa_long')}
        </Trans>
      );
      buttons = isMarketplace ? (
        <>{SupprimerErrorButton}</>
      ) : (
        <>
          {RemplacerErrorButton}
          {DiffererErrorButton}
          {SupprimerErrorButton}
        </>
      );
      break;
    }
    // Article plus au catalogue : remplacer ou supprimer (pas de différer car le produit ne sera jamais disponible)
    case 'article_inconnu':
    case 'article_obsolete':
    case 'non_commercialise':
    case 'non_autorise':
      // Message simple pour le client, la raison précise de Tolede reste dans le tooltip
      message = probleme ? (
        tooltip(
          `${probleme.problem.message} (${probleme.problem.code})`,
          t('ligne_produit.indisponible.article_indisponible'),
        )
      ) : (
        <Trans t={t}>
          {t('ligne_produit.indisponible.article_indisponible')}
        </Trans>
      );
      // Remplacer seulement si la ligne porte le lien (absent sur une ligne marketplace, que
      // isMarketplace ne reconnaît pas quand son item du catalogue ne se charge plus), et pas
      // pour article_inconnu : Tolede ne connaît plus l'article, il n'y a rien sur quoi chercher
      // un remplaçant
      buttons = (
        <>
          {errorCode !== 'article_inconnu' &&
          ligneDePanier.hasOneLink('c:remplacer-un-article')
            ? RemplacerErrorButton
            : null}
          {SupprimerErrorButton}
        </>
      );
      break;
    case 'prix_plancher': // TODO #1734
    case 'non_web':
    case 'erreur_temperature_tournee':
    case 'erreur_multiple_de_commande':
    case 'generique':
    case 'heure_depassee':
    case 'commande_deja_validee':
    case 'adresse_inconnue': // A partir de là normalement cela ne concerne que la commande
    case 'client_bloque':
    case 'client_inconnu':
    case 'erreur_code_tournee':
    case 'erreur_date_livraison':
    case 'erreur_mode_de_rendu':
    case 'erreur_nb_lignes':
    case 'franco_poids_prix_non_atteint':
    case 'franco_marketplace':
    case 'hors_delai':
    case 'reference':
    case 'delai_livraison_trop_long_cb':
    case 'panier_sans_ligne':
      buttons = (
        <>
          {/* {RemplacerErrorButton} */}
          {SupprimerErrorButton}
        </>
      );
      // Sans problème de contrôle commande, pas de message/code Tolede à afficher en tooltip
      message = probleme ? (
        tooltip(
          `${probleme.problem.message} (${probleme.problem.code})`,
          t([
            `error.${probleme.problem.code}`,
            'ligne_produit.indisponible.generique',
          ]),
        )
      ) : (
        <Trans t={t}>{t('ligne_produit.indisponible.generique')}</Trans>
      );
      break;
  }

  return {message, buttons};
};
